import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { TextBlock } from '../../../Atoms/Text';
import { SearchBox } from '../../Search/Search';
import { createTsdMap } from '../../../../store/TsdMap';
import { createApMap } from '../../../../store/ApMap';
import { createSupplierMap, SupplierMap } from '../../../../store/SupplierMap';
import { EntityType, SearchResult } from '../../../../store/Search';
import { getCurrentUser } from '../../../../store/User';
import { createProductMap, ProductMap } from '../../../../store/ProductMap';
import { Product } from '../../../../store/Product';

export interface ProductPrefill {
  familyId?: string;
  productSfId?: string;
  rpmSupplierId?: number;
  rpmSupplierName?: string;
  rpmProductId?: number;
  rpmProductName?: string;
}

export interface NewMapDialogParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  inputString?: string;
  preFill?: 'SFDC' | 'RPM' | '';
  fetch?: (id: string) => Promise<SupplierMap[] | ProductMap[] | undefined>;
  id?: string;
  mapType: EntityType.TSD | EntityType.SUPPLIER | EntityType.AP | EntityType.PRODUCT;
  allProducts?: Product[];
  productPrefill?: ProductPrefill;
  productMaps?: ProductMap[];
  disabledProductPrefill?: boolean;
}

interface FormData {
  sfUpstackId: string;
  rpmId: string;
  sfName: string;
  rpmName: string;
  rpmRepId?: string;
  reviewRequired: boolean;
  note: string;
  family?: string;
  rpmProductId?: number;
  rpmProductName?: string;
}

export const NewMapDialog = ({
  open,
  setOpen,
  mapType,
  inputString,
  preFill = '',
  fetch,
  id,
  allProducts = [],
  productPrefill,
  productMaps,
  disabledProductPrefill = false
}: NewMapDialogParams) => {
  const { setSnack } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    sfUpstackId: '',
    rpmId: '',
    sfName: '',
    rpmName: '',
    rpmRepId: '',
    reviewRequired: false,
    note: '',
    family: '',
    rpmProductId: undefined,
    rpmProductName: ''
  });
  const user = getCurrentUser();
  const productFamilies = [...new Set(allProducts.map((product) => product.family))];
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  useEffect(() => {
    setFormData({
      sfUpstackId: '',
      rpmId: '',
      sfName: '',
      rpmName: '',
      rpmRepId: '',
      reviewRequired: false,
      note: '',
      family: '',
      rpmProductId: undefined,
      rpmProductName: ''
    });
    if (mapType === EntityType.PRODUCT) {
      if (preFill === 'SFDC') {
        setFilteredProducts(allProducts.filter((product) => product.family === productPrefill?.familyId));
        setFormData((prevData) => ({
          ...prevData,
          sfUpstackId: productPrefill?.productSfId || '',
          family: productPrefill?.familyId || ''
        }));
      } else if (preFill === 'RPM') {
        setFormData((prevData) => ({
          ...prevData,
          rpmId: productPrefill?.rpmSupplierId?.toString() || '',
          rpmName: productPrefill?.rpmSupplierName || '',
          rpmProductId: productPrefill?.rpmProductId,
          rpmProductName: productPrefill?.rpmProductName || ''
        }));
      }
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (result: SearchResult) => {
    const { source, id, title } = result;
    if (['ap', 'tsd', 'supplier'].includes(source)) {
      setFormData((prevData) => ({ ...prevData, sfUpstackId: id, sfName: title }));
    } else if (source === 'rpm_rep') {
      setFormData((prevData) => ({ ...prevData, rpmRepId: id }));
    } else if (source === 'rpm_product') {
      setFormData((prevData) => ({ ...prevData, rpmProductId: Number.parseInt(id), rpmProductName: title }));
    } else {
      setFormData((prevData) => ({ ...prevData, rpmId: id, rpmName: title }));
    }
  };

  const handleClearSelect = (field: string) => {
    if (field in formData) {
      setFormData((prevData) => ({ ...prevData, [field]: '' }));
    }
    if (mapType === EntityType.PRODUCT && field === 'rpmId') {
      setFormData((prevData) => ({ ...prevData, rpmProductId: undefined, rpmProductName: '' }));
    }
  };

  const handleSubmitForm = async () => {
    if (!user) return;

    const mapConfig = {
      [EntityType.TSD]: {
        entityType: EntityType.TSD,
        createFn: createTsdMap,
        mapDataKeys: {
          tsd_id: 'sfUpstackId',
          tsd_rpm: 'rpmName'
        }
      },
      [EntityType.AP]: {
        entityType: EntityType.AP,
        createFn: createApMap,
        mapDataKeys: {
          ap_id: 'sfUpstackId',
          ap_rpm: 'rpmName',
          ap_rpm_rep_id: 'rpmRepId'
        }
      },
      [EntityType.SUPPLIER]: {
        entityType: EntityType.SUPPLIER,
        createFn: createSupplierMap,
        mapDataKeys: {
          supplier_id: 'sfUpstackId',
          rpm_supplier_id: 'rpmId',
          note: 'note'
        }
      },
      [EntityType.PRODUCT]: {
        entityType: EntityType.PRODUCT,
        createFn: createProductMap,
        mapDataKeys: {
          productId: 'sfUpstackId',
          rpmProductId: 'rpmProductId',
          reviewRequired: 'reviewRequired',
          note: 'note'
        }
      }
    }[mapType];

    if (!mapConfig) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newMap: any = {};

    for (const [entityKey, formKey] of Object.entries(mapConfig.mapDataKeys)) {
      newMap[entityKey] = formData[formKey as keyof FormData];
    }

    const { data, error } = await mapConfig.createFn(newMap);

    if (data) {
      setSnack({ message: 'Mapping was created successfully', type: 'success', open: true });
      if (fetch && id) fetch(id);
      handleClose();
    } else if (error) {
      setSnack({ message: `There was an error creating mapping: \n${error.message}`, type: 'error', open: true });
    }
  };

  const handleChange = (fieldName: keyof FormData, value: string | boolean) => {
    if (fieldName === 'family' && formData.family !== value) {
      setFilteredProducts(allProducts.filter((product) => product.family === value));
      setFormData((prevData) => ({
        ...prevData,
        family: typeof value === 'string' ? value : '',
        sfUpstackId: ''
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
    }
  };

  const getLabel = () => {
    switch (mapType) {
      case EntityType.TSD:
        return { label: 'TSD', rpmLabel: 'Source' };
      case EntityType.AP:
        return { label: 'AP', rpmLabel: 'Agency Group' };
      case EntityType.PRODUCT:
        return { label: 'Product', rpmLabel: 'Supplier' };
      case EntityType.SUPPLIER:
      default:
        return { label: 'Supplier', rpmLabel: 'Supplier' };
    }
  };

  const { label, rpmLabel } = getLabel();
  const disabledSave =
    !formData.sfUpstackId ||
    (mapType !== EntityType.TSD && !formData.rpmName) ||
    (mapType === EntityType.PRODUCT && !formData.rpmProductId);

  return (
    <Dialog
      data-testid="new-map-dialog"
      maxWidth="md"
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: { sx: { overflowY: 'visible', overflowX: 'hidden' } }
      }}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New {label} Mapping
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, overflowY: 'visible', overflowX: 'hidden', position: 'relative' }}>
        <div className="flex flex-row gap-x-3 w-full">
          {/* Left side Salesforce column */}
          <div
            className="flex flex-col gap-y-3 w-1/2"
            style={{ minWidth: '18rem' }}>
            {mapType !== EntityType.PRODUCT && (
              <div
                className="space-y-3 w-full"
                style={{ position: 'relative', zIndex: 1 }}>
                <TextBlock color="grey6">Salesforce {label}</TextBlock>
                <SearchBox
                  fromModal={true}
                  handleSelect={handleSelect}
                  handleClearSelect={() => handleClearSelect('sfUpstackId')}
                  searchEntityType={mapType}
                  inputString={preFill === 'SFDC' ? inputString : ''}
                  dropdownZIndex={1300}
                />
              </div>
            )}
            {mapType === EntityType.PRODUCT && (
              <div className="space-y-3 w-full">
                {formData.family}
                <TextBlock color="grey6">Salesforce Product Family</TextBlock>
                <Autocomplete
                  options={productFamilies}
                  value={formData.family || ''}
                  sx={{
                    width: '18rem',
                    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                      paddingLeft: '14px'
                    }
                  }}
                  onChange={(_, newValue) => handleChange('family', newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ width: '100%' }}
                      slotProps={{
                        ...params.slotProps,
                        input: {
                          ...params.slotProps.input,
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ mr: 0 }}>
                              <SearchIcon color="action" />
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                  )}
                  disableClearable={false}
                  isOptionEqualToValue={(option, value) => option === value}
                />
              </div>
            )}
            {mapType === EntityType.PRODUCT && (
              <div className="space-y-3 w-full">
                <TextBlock color="grey6">Salesforce Product</TextBlock>
                <Autocomplete
                  options={filteredProducts}
                  getOptionLabel={(option) => option.name || ''}
                  value={filteredProducts.find((p) => p.id === formData.sfUpstackId) || null}
                  sx={{ width: '18rem', '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { paddingLeft: '14px' } }}
                  onChange={(_, newValue) => handleChange('sfUpstackId', newValue ? newValue.id : '')}
                  disabled={!formData.family || formData.family === ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ width: '100%' }}
                      slotProps={{
                        ...params.slotProps,
                        input: {
                          ...params.slotProps.input,
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ mr: 0 }}>
                              <SearchIcon color="action" />
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                  )}
                  disableClearable={false}
                  isOptionEqualToValue={(option, value) => option.sf_id === value.sf_id}
                />
              </div>
            )}
          </div>
          {/* Right side RPM column */}
          <div
            className="flex flex-col gap-y-3 w-1/2"
            style={{ minWidth: '18rem' }}>
            <div
              className="space-y-3 w-full"
              style={{ position: 'relative', zIndex: 1 }}>
              <TextBlock color="grey6">RPM {rpmLabel}</TextBlock>
              {(mapType === EntityType.SUPPLIER || mapType === EntityType.PRODUCT) &&
                (disabledProductPrefill ? (
                  <TextBlock
                    color="black"
                    className="pt-1 pb-2">
                    {productPrefill?.rpmSupplierName}
                  </TextBlock>
                ) : (
                  <SearchBox
                    fromModal={true}
                    handleSelect={handleSelect}
                    handleClearSelect={() => {
                      handleClearSelect('rpmId');
                      handleClearSelect('rpmName');
                    }}
                    searchEntityType={EntityType.RPM_SUPPLIER}
                    inputString={preFill === 'RPM' ? (productPrefill?.rpmSupplierName ?? inputString) : ''}
                    dropdownZIndex={1300}
                  />
                ))}
              {mapType === EntityType.PRODUCT && (
                <div className="space-y-3 w-full">
                  <TextBlock color="grey6">RPM Product</TextBlock>
                  {disabledProductPrefill ? (
                    <TextBlock
                      color="black"
                      className="pt-1 pb-2">
                      {productPrefill?.rpmProductName}
                    </TextBlock>
                  ) : (
                    <SearchBox
                      fromModal={true}
                      handleSelect={handleSelect}
                      handleClearSelect={() => handleClearSelect('rpmProductId')}
                      inputString={preFill === 'RPM' ? productPrefill?.rpmProductName : ''}
                      searchEntityType={EntityType.RPM_PRODUCT}
                      disabled={!formData.rpmId}
                      filters={
                        formData.rpmId
                          ? {
                              product_supplier_id: formData.rpmId,
                              excludeIds: productMaps?.map((map) => map.rpmProductId).join(',') || ''
                            }
                          : undefined
                      }
                      dropdownZIndex={1300}
                    />
                  )}
                </div>
              )}
              {mapType !== EntityType.SUPPLIER && mapType !== EntityType.PRODUCT && (
                <div className="flex gap-1">
                  <TextField
                    sx={{ width: '100%' }}
                    value={formData.rpmName || ''}
                    onChange={(e) => handleChange('rpmName', e?.target?.value)}
                    size="small"
                  />
                </div>
              )}
            </div>
            {mapType === EntityType.AP && (
              <div className="space-y-3 w-full">
                <TextBlock color="grey6">RPM Rep Assignment</TextBlock>
                <SearchBox
                  fromModal={true}
                  handleSelect={handleSelect}
                  handleClearSelect={() => {
                    handleClearSelect('repName');
                    handleClearSelect('repId');
                  }}
                  searchEntityType={EntityType.RPM_REP}
                  inputString=""
                  dropdownZIndex={1300}
                />
              </div>
            )}
          </div>
        </div>
        {/* Additional fields for mapping */}
        <div className="flex flex-col gap-y-3 w-full mt-3">
          {mapType === EntityType.PRODUCT && (
            <div className="flex items-center w-full">
              <TextBlock color="grey6">Review Required?</TextBlock>
              <Checkbox
                checked={formData.reviewRequired}
                onChange={(e) => handleChange('reviewRequired', e.target.checked)}
              />
            </div>
          )}
          {(mapType === EntityType.SUPPLIER || mapType === EntityType.PRODUCT) && (
            <div className="space-y-3 w-full">
              <TextBlock color="grey6">Note</TextBlock>
              <TextField
                sx={{ width: '100%' }}
                value={formData.note || ''}
                onChange={(e) => handleChange('note', e?.target?.value)}
                size="small"
                multiline
                rows={3}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          Cancel
        </button>
        <button
          data-testid="submit-new-link"
          disabled={disabledSave}
          onClick={handleSubmitForm}
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          type="submit">
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};
